// src/pages/Cabling.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { LoadingSpinner } from '../App';
import { COLORS, SHEET_CONFIG, getSheetCsvUrl } from '../constants';
import RefreshButton from '../components/ui/RefreshButton';
import { CablingProgressTable } from '../components/cabling';

const ROWS_PER_PAGE = 50;

const normalizeCode = (value) =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '')
    .replace(/[-_/]/g, '');

const parseCsv = (csvText) => {
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const ch = csvText[i];
    const next = csvText[i + 1];

    if (ch === '"' && next === '"') {
      cell += '"';
      i++;
    } else if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      row.push(cell.trim());
      cell = '';
    } else if ((ch === '\n' || ch === '\r') && !inQuotes) {
      if (ch === '\r' && next === '\n') i++;
      row.push(cell.trim());
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }

  if (cell || row.length) {
    row.push(cell.trim());
    rows.push(row);
  }

  return rows.filter((r) => r.some((c) => String(c).trim() !== ''));
};

const findIndex = (headers, patterns) =>
  headers.findIndex((header) =>
    patterns.some((pattern) => pattern.test(String(header || '').trim()))
  );

const loadSheetRows = async (sheetId, gid, label) => {
  const url = getSheetCsvUrl(sheetId, {
    gid,
    bustCache: true,
  });

  console.log(`Fetching ${label}:`, url);

  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(
      `${label} fetch failed. Status ${response.status}. Check sheet ID, GID, and sharing access.`
    );
  }

  const text = await response.text();

  if (text.toLowerCase().includes('<html')) {
    throw new Error(
      `${label} returned Google login/HTML page. Share the sheet as Anyone with link → Viewer.`
    );
  }

  return parseCsv(text);
};

const mapBomRows = (csvRows) => {
  if (!csvRows.length) return [];

  const headers = csvRows[0];

  const itemNoIndex = findIndex(headers, [/item\s*no/i, /^s\.?no$/i, /^no$/i]);
  const productCodeIndex = findIndex(headers, [
    /product\s*code/i,
    /bundle/i,
    /cable/i,
    /part\s*no/i,
    /item\s*code/i,
  ]);
  const qtyIndex = findIndex(headers, [/^qty$/i, /quantity/i, /count/i]);

  return csvRows
    .slice(1)
    .map((row, index) => {
      const productCode = row[productCodeIndex] || '';

      return {
        itemNo: row[itemNoIndex] || index + 1,
        productCode,
        bundle: productCode,
        qty: Number(row[qtyIndex] || 0) || 0,
        matchKey: normalizeCode(productCode),
      };
    })
    .filter((row) => row.productCode && row.matchKey);
};

const mapProgressRows = (csvRows) => {
  if (!csvRows.length) return [];

  const headers = csvRows[0];

  const itemNoIndex = findIndex(headers, [/item\s*no/i, /^s\.?no$/i, /^no$/i]);
  const productCodeIndex = findIndex(headers, [
    /product\s*code/i,
    /bundle/i,
    /cable/i,
    /part\s*no/i,
    /item\s*code/i,
  ]);
  const qtyIndex = findIndex(headers, [/^qty$/i, /quantity/i, /count/i]);
  const roughedIndex = findIndex(headers, [/roughed\s*in/i, /roughed/i]);
  const dressedIndex = findIndex(headers, [/dressed\s*in/i, /dressed/i]);
  const testedIndex = findIndex(headers, [/tested/i]);
  const terminatedIndex = findIndex(headers, [/terminated/i, /term/i]);

  return csvRows
    .slice(1)
    .map((row, index) => {
      const productCode = row[productCodeIndex] || '';

      return {
        itemNo: row[itemNoIndex] || index + 1,
        productCode,
        bundle: productCode,
        qty: Number(row[qtyIndex] || 0) || 0,
        roughedIn: Number(row[roughedIndex] || 0) || 0,
        dressedIn: Number(row[dressedIndex] || 0) || 0,
        tested: Number(row[testedIndex] || 0) || 0,
        terminated: Number(row[terminatedIndex] || 0) || 0,
        matchKey: normalizeCode(productCode),
      };
    })
    .filter((row) => row.productCode && row.matchKey);
};

const mergeBomWithProgress = (bomRows, progressRows, type) => {
  const progressMap = new Map();

  progressRows.forEach((row) => {
    if (row.matchKey) {
      progressMap.set(row.matchKey, row);
    }
  });

  return bomRows.map((bomRow, index) => {
    const matched = progressMap.get(bomRow.matchKey);

    const qty = Number(bomRow.qty || matched?.qty || 0) || 0;
    const roughedIn = Number(matched?.roughedIn || 0) || 0;
    const dressedIn = Number(matched?.dressedIn || 0) || 0;
    const tested = Number(matched?.tested || 0) || 0;
    const terminated = Number(matched?.terminated || 0) || 0;

    const completed =
      type === 'fibre'
        ? Math.max(roughedIn, dressedIn, tested, terminated)
        : Math.max(roughedIn, dressedIn);

    return {
      itemNo: bomRow.itemNo || index + 1,
      productCode: bomRow.productCode,
      bundle: bomRow.bundle || bomRow.productCode,
      qty,
      count: qty,
      roughedIn,
      dressedIn,
      tested,
      terminated,
      completed,
      remaining: Math.max(0, qty - completed),
      status: matched ? 'Updated' : 'Not Updated',
      cablingUpdated: Boolean(matched),
      fibreUpdated: Boolean(matched),
    };
  });
};

export default function Cabling() {
  const [copperBomRows, setCopperBomRows] = useState([]);
  const [copperProgressRows, setCopperProgressRows] = useState([]);
  const [fibreBomRows, setFibreBomRows] = useState([]);
  const [fibreProgressRows, setFibreProgressRows] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');

  const [searchCopper, setSearchCopper] = useState('');
  const [searchFibre, setSearchFibre] = useState('');
  const [copperPage, setCopperPage] = useState(1);
  const [fibrePage, setFibrePage] = useState(1);

  const loadData = async () => {
    try {
      setError('');
      setLoading(true);

      const [copperBomCsv, copperProgressCsv, fibreBomCsv, fibreProgressCsv] =
        await Promise.all([
          loadSheetRows(
            SHEET_CONFIG.CABLING_BOM_SHEET,
            SHEET_CONFIG.CABLING_BOM_SHEET_GID,
            'Copper BOM sheet'
          ),
          loadSheetRows(
            SHEET_CONFIG.CABLING_SHEET,
            SHEET_CONFIG.CABLING_SHEET_GID,
            'Copper Cabling sheet'
          ),
          loadSheetRows(
            SHEET_CONFIG.FIBRE_CABLING_BOM_SHEET,
            SHEET_CONFIG.FIBRE_CABLING_BOM_SHEET_GID,
            'Fibre BOM sheet'
          ),
          loadSheetRows(
            SHEET_CONFIG.FIBRE_CABLING_SHEET,
            SHEET_CONFIG.FIBRE_CABLING_SHEET_GID,
            'Fibre Cabling sheet'
          ),
        ]);

      setCopperBomRows(mapBomRows(copperBomCsv));
      setCopperProgressRows(mapProgressRows(copperProgressCsv));
      setFibreBomRows(mapBomRows(fibreBomCsv));
      setFibreProgressRows(mapProgressRows(fibreProgressCsv));
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to load cabling data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const refreshData = () => {
    setRefreshing(true);
    loadData();
  };

  const mergedCopperData = useMemo(
    () => mergeBomWithProgress(copperBomRows, copperProgressRows, 'copper'),
    [copperBomRows, copperProgressRows]
  );

  const mergedFibreData = useMemo(
    () => mergeBomWithProgress(fibreBomRows, fibreProgressRows, 'fibre'),
    [fibreBomRows, fibreProgressRows]
  );

  const filteredCopperData = useMemo(() => {
    if (!searchCopper) return mergedCopperData;
    const term = searchCopper.toLowerCase();

    return mergedCopperData.filter((item) =>
      String(item.productCode || '').toLowerCase().includes(term) ||
      String(item.status || '').toLowerCase().includes(term) ||
      String(item.itemNo || '').toLowerCase().includes(term)
    );
  }, [mergedCopperData, searchCopper]);

  const filteredFibreData = useMemo(() => {
    if (!searchFibre) return mergedFibreData;
    const term = searchFibre.toLowerCase();

    return mergedFibreData.filter((item) =>
      String(item.productCode || '').toLowerCase().includes(term) ||
      String(item.bundle || '').toLowerCase().includes(term) ||
      String(item.status || '').toLowerCase().includes(term) ||
      String(item.itemNo || '').toLowerCase().includes(term)
    );
  }, [mergedFibreData, searchFibre]);

  const paginatedCopperData = useMemo(() => {
    const start = (copperPage - 1) * ROWS_PER_PAGE;
    return filteredCopperData.slice(start, start + ROWS_PER_PAGE);
  }, [filteredCopperData, copperPage]);

  const paginatedFibreData = useMemo(() => {
    const start = (fibrePage - 1) * ROWS_PER_PAGE;
    return filteredFibreData.slice(start, start + ROWS_PER_PAGE);
  }, [filteredFibreData, fibrePage]);

  const copperTotalPages = Math.ceil(filteredCopperData.length / ROWS_PER_PAGE);
  const fibreTotalPages = Math.ceil(filteredFibreData.length / ROWS_PER_PAGE);

  const copperUpdatedCount = mergedCopperData.filter((r) => r.cablingUpdated).length;
  const copperPendingCount = mergedCopperData.filter((r) => !r.cablingUpdated).length;
  const fibreUpdatedCount = mergedFibreData.filter((r) => r.fibreUpdated).length;
  const fibrePendingCount = mergedFibreData.filter((r) => !r.fibreUpdated).length;

  if (loading) {
    return (
      <LoadingSpinner
        title="Loading Cabling Data"
        subtitle="Comparing Copper and Fibre BOM sheets with progress sheets..."
      />
    );
  }

  if (error) {
    return (
      <div style={{ color: COLORS.red, padding: 60, textAlign: 'center' }}>
        Error loading cabling data: {error}
      </div>
    );
  }

  return (
    <>
      <style>{`
        .cabling-page {
          height: 100%;
          width: 100%;
          background: #f8fafc;
          overflow: hidden;
          box-sizing: border-box;
        }

        .cabling-container {
          width: 100%;
          height: 100%;
          padding: 12px 14px;
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        .cabling-stats-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 12px;
          margin-bottom: 18px;
        }

        .cabling-stats-card {
          background: white;
          border-radius: 12px;
          padding: 10px 14px;
          border: 1px solid #e2e8f0;
        }

        .cabling-stats-label {
          font-size: 11px;
          color: #64748b;
          margin-bottom: 4px;
          font-weight: 500;
        }

        .cabling-stats-value {
          font-size: 22px;
          font-weight: 800;
        }

        .cabling-stats-sub {
          font-size: 10px;
          color: #94a3b8;
          margin-top: 4px;
        }

        .cabling-tables-area {
          flex: 1;
          min-height: 0;
          overflow: auto;
          width: 100%;
        }

        .cabling-section {
          width: 100%;
          margin-bottom: 24px;
        }

        .cabling-section-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e2e8f0;
        }

        .cabling-section-title {
          font-size: 14px;
          font-weight: 800;
          color: #0f172a;
        }

        .cabling-section-count {
          font-size: 11px;
          color: #64748b;
          font-weight: 600;
        }

        .cabling-search-input {
          height: 34px;
          padding: 0 12px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          font-size: 12px;
          width: 260px;
          background: white;
          outline: none;
        }

        .cabling-table-wrapper {
          width: 100%;
          max-height: 430px;
          overflow: auto;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 14px;
          position: relative;
        }

        .cabling-table-wrapper table {
          width: 100%;
          border-collapse: separate !important;
          border-spacing: 0 !important;
        }

        .cabling-table-wrapper thead th {
          position: sticky !important;
          top: 0;
          z-index: 100;
          background: #f1f5f9 !important;
          box-shadow: 0 1px 0 #e2e8f0, 0 4px 10px rgba(15, 23, 42, 0.08);
        }

        .cabling-pagination {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 8px;
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px solid #e2e8f0;
        }

        .cabling-pagination button {
          padding: 5px 12px;
          font-size: 11px;
          font-weight: 700;
          border-radius: 7px;
          border: 1px solid #e2e8f0;
          background: white;
          cursor: pointer;
        }

        .cabling-pagination button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .cabling-pagination span {
          font-size: 11px;
          color: #64748b;
        }
      `}</style>

      {refreshing && (
        <LoadingSpinner title="Refreshing Cabling Data" subtitle="Updating latest records..." />
      )}

      <div className="cabling-page">
        <div className="cabling-container">
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            <RefreshButton onClick={refreshData} refreshing={refreshing} size="sm" />
          </div>

          <div className="cabling-stats-grid">
            <div className="cabling-stats-card">
              <div className="cabling-stats-label">Copper BOM</div>
              <div className="cabling-stats-value" style={{ color: COLORS.primary }}>
                {copperBomRows.length}
              </div>
              <div className="cabling-stats-sub">Copper BOM items</div>
            </div>

            <div className="cabling-stats-card">
              <div className="cabling-stats-label">Copper Updated</div>
              <div className="cabling-stats-value" style={{ color: COLORS.success }}>
                {copperUpdatedCount}
              </div>
              <div className="cabling-stats-sub">Matched copper rows</div>
            </div>

            <div className="cabling-stats-card">
              <div className="cabling-stats-label">Copper Pending</div>
              <div className="cabling-stats-value" style={{ color: COLORS.warning }}>
                {copperPendingCount}
              </div>
              <div className="cabling-stats-sub">Not updated</div>
            </div>

            <div className="cabling-stats-card">
              <div className="cabling-stats-label">Fibre Updated</div>
              <div className="cabling-stats-value" style={{ color: COLORS.cyan }}>
                {fibreUpdatedCount}
              </div>
              <div className="cabling-stats-sub">Matched fibre rows</div>
            </div>

            <div className="cabling-stats-card">
              <div className="cabling-stats-label">Fibre Pending</div>
              <div className="cabling-stats-value" style={{ color: COLORS.orange }}>
                {fibrePendingCount}
              </div>
              <div className="cabling-stats-sub">Not updated</div>
            </div>
          </div>

          <div className="cabling-tables-area">
            <div className="cabling-section">
              <div className="cabling-section-header">
                <span className="cabling-section-title">Copper Cabling - BOM Comparison</span>
                <span className="cabling-section-count">{filteredCopperData.length} items</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <input
                  className="cabling-search-input"
                  placeholder="Search copper..."
                  value={searchCopper}
                  onChange={(e) => {
                    setSearchCopper(e.target.value);
                    setCopperPage(1);
                  }}
                />
              </div>

              <div className="cabling-table-wrapper">
                <CablingProgressTable
                  rows={paginatedCopperData}
                  type="copper"
                  title="Copper Cabling"
                  subtitle="Copper BOM compared with copper cabling progress sheet"
                />
              </div>

              {copperTotalPages > 1 && (
                <div className="cabling-pagination">
                  <button disabled={copperPage === 1} onClick={() => setCopperPage((p) => p - 1)}>
                    Previous
                  </button>
                  <span>Page {copperPage} of {copperTotalPages}</span>
                  <button
                    disabled={copperPage === copperTotalPages}
                    onClick={() => setCopperPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>

            <div className="cabling-section">
              <div className="cabling-section-header">
                <span className="cabling-section-title">Fibre Cabling - BOM Comparison</span>
                <span className="cabling-section-count">{filteredFibreData.length} items</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <input
                  className="cabling-search-input"
                  placeholder="Search fibre..."
                  value={searchFibre}
                  onChange={(e) => {
                    setSearchFibre(e.target.value);
                    setFibrePage(1);
                  }}
                />
              </div>

              <div className="cabling-table-wrapper">
                <CablingProgressTable
                  rows={paginatedFibreData}
                  type="fibre"
                  title="Fibre Cabling"
                  subtitle="Fibre BOM compared with fibre cabling progress sheet"
                />
              </div>

              {fibreTotalPages > 1 && (
                <div className="cabling-pagination">
                  <button disabled={fibrePage === 1} onClick={() => setFibrePage((p) => p - 1)}>
                    Previous
                  </button>
                  <span>Page {fibrePage} of {fibreTotalPages}</span>
                  <button
                    disabled={fibrePage === fibreTotalPages}
                    onClick={() => setFibrePage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
